import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoImage from "@assets/favicon new no background cc6633.png";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, getCsrfHeaders, setCsrfToken } from "@/lib/queryClient";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { Loader2, Truck, Package, Users } from "lucide-react";
import { Link, useSearch } from "wouter";

export default function Register() {
  useCsrfToken();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const preselectedRole = searchParams.get("role");
  const refCode = searchParams.get("ref") || "";
  const { toast } = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>(
    preselectedRole && ["shipper", "carrier", "broker"].includes(preselectedRole)
      ? preselectedRole
      : ""
  );
  const [roleError, setRoleError] = useState(false);

  const registerMutation = useMutation({
    mutationFn: async (data: { email: string; password: string; firstName: string; lastName: string; userType: string; referralCode?: string }) => {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Registration failed");
      }
      return response.json();
    },
    onSuccess: async (responseData) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

      // Consume partner token if present
      const partnerToken = new URLSearchParams(window.location.search).get("partnerToken");
      if (partnerToken) {
        try {
          // Registration creates a new session which invalidates the previous CSRF token.
          // Fetch and cache a fresh one before calling accept-token.
          const csrfRes = await fetch("/api/csrf-token", { credentials: "include" });
          const csrfData = await csrfRes.json();
          setCsrfToken(csrfData.csrfToken);

          const response = await fetch("/api/partner-invites/accept-token", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
            body: JSON.stringify({ token: partnerToken }),
          });
          const data = await response.json();
          if (data.success && data.loadId) {
            window.location.href = `/find-loads?highlight=${data.loadId}`;
            return;
          }
        } catch (err) {
          console.error("Failed to consume partner token:", err);
          // Non-fatal — continue to normal post-registration flow
        }
      }

      // Build query params from referralPartner signal if present
      const rp = responseData?.referralPartner;
      let selectMembershipUrl = `/select-membership/${selectedRole}`;
      if (rp?.partnered === true && rp.partnerName) {
        selectMembershipUrl += `?connected=1&partnerName=${encodeURIComponent(rp.partnerName)}`;
      } else if (rp?.partnered === false && rp.reason === "SAME_ROLE") {
        selectMembershipUrl += `?connected=samerole`;
      }

      window.location.href = selectMembershipUrl;
    },
    onError: (error: Error) => {
      toast({
        title: "Registration Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
      if (!selectedRole) {
        setRoleError(true);
        return;
      }

      if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }
    
    if (password.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters.",
        variant: "destructive",
      });
      return;
    }
    
    const userType = selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1);
    registerMutation.mutate({ email, password, firstName, lastName, userType, referralCode: refCode || undefined });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-6">
          <Link href="/" className="flex justify-center" data-testid="link-register-logo">
            <img src={logoImage} alt="Fill The Trucks" className="h-20" />
          </Link>
          <div>
            <CardTitle className="text-3xl font-bold tracking-wide mb-2">Create Your Account</CardTitle>
            <CardDescription className="text-base">Start free — no credit card required for the free plan.</CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>I am a...</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { role: "shipper", label: "Shipper", sub: "Post Loads", sub2: "Find Trucks", Icon: Package },
{ role: "carrier", label: "Carrier", sub: "Find Loads", sub2: "Post Capacity", Icon: Truck },
{ role: "broker", label: "Broker", sub: "Connect", sub2: "both sides", Icon: Users },
                    ].map(({ role, label, sub, sub2, Icon }) => (
                    <button
                      key={role}
                      type="button"
                      data-testid={`button-role-${role}`}
                      onClick={() => { setSelectedRole(role); setRoleError(false); }}
                      className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors cursor-pointer
                        ${selectedRole === role
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center
                        ${selectedRole === role ? "bg-primary/20" : "bg-muted"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{label}</span>
                      <span className="text-xs text-muted-foreground">{sub}</span>
                      <span className="text-xs text-muted-foreground">{sub2}</span>
                    </button>
                  ))}
                </div>
                {roleError && (
                  <p className="text-sm text-destructive">Please select an account type</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Business Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="input-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="input-confirm-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base"
              size="lg"
              disabled={registerMutation.isPending}
              data-testid="button-register"
            >
              {registerMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Create Your Account"
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link href="/login" className="text-primary hover:underline font-medium" data-testid="link-login">
                Sign in
              </Link>
            </p>
          </div>

          <div className="text-center text-xs text-muted-foreground">
            <p>
              By creating an account, you agree to our{" "}
              <Link href="/terms-of-use" className="hover:text-foreground underline" data-testid="link-terms">Terms of Service</Link>
              {" "}and{" "}
              <Link href="/privacy-policy" className="hover:text-foreground underline" data-testid="link-privacy">Privacy Policy</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
